import { Component, OnInit, OnDestroy } from '@angular/core';
import { Cluster } from 'src/app/models/cluster.model';
import { Subscription } from 'rxjs';
import { ClustersService } from 'src/app/services/clusters.service';
import { Product } from 'src/app/models/product.model';
import { parse } from 'papaparse';
import { FundDescription } from 'src/app/models/fund-description.model';
import { NotificationsService } from 'src/app/services/notifications.service';

export class ManagedFund {
  original_data: FundDescription;
  updated_data: Product;
}

@Component({
  selector: 'app-my-portfolio-update',
  templateUrl: './my-portfolio-update.component.html',
  styleUrls: ['./my-portfolio-update.component.scss']
})
export class MyPortfolioUpdateComponent implements OnInit, OnDestroy {

  currentCluster: Cluster;
  currentClusterSubscription: Subscription;

  disableUpdate: boolean = true;
  disableActions: boolean = false;
  fileUploadLacking: boolean = false;
  fileUploadNeeded: boolean = false;
  hasInvestable: boolean = true;

  uploadedProducts: Product[] = [];

  managedOriginal: ManagedFund;
  managedBenchmark: ManagedFund;
  managedComponents: ManagedFund[];

  alertCodes: any = {
    import: [],
    update: [],
  };

  constructor(private clustersService: ClustersService,
              private notificationsService: NotificationsService) {}

  ngOnInit(): void {
    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (currentCluster: Cluster) => {
        this.currentCluster = currentCluster;
        if (currentCluster) {
          this.onClusterChange(currentCluster);
          // Compute flags
          this.onUpdateSelectionChange
        }
      }
    );
    this.clustersService.emitCurrentCluster();
  }

  ngOnDestroy() {
    this.currentClusterSubscription.unsubscribe();
  }

  changeListener(files: FileList){
    if (files && files.length > 0) {
      this.parseFiles(files).then(
        () => {this.notificationsService.notify('data/import-success');},
        () => {this.notificationsService.notify('data/import-failed');},
      );
    }
  }

  formatDate(dateStr: string) {
    let day: string, month: string, year: string;
    if (/^[0-3][0-9]\/[0-1][0-9]\/[0-2][0-9][0-9][0-9]$/.test(dateStr)) { // FORMAT dd/MM/yyyy
      const res = /^([0-3][0-9])\/([0-1][0-9])\/([0-2][0-9][0-9][0-9])$/.exec(dateStr);
      day = res[1]; month = res[2]; year = res[3];
    } else if (/^[0-2][0-9][0-9][0-9]\-[0-1][0-9]\-[0-3][0-9]$/.test(dateStr)) { // FORMAT yyyy-MM-dd
      const res = /^([0-2][0-9][0-9][0-9])\-([0-1][0-9])\-([0-3][0-9])$/.exec(dateStr);
      day = res[3]; month = res[2]; year = res[1];
    } else if (/^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9]$/.test(dateStr)) { // FORMAT dd/MM/yy
      const res = /^([0-3][0-9])\/([0-1][0-9])\/([0-9][0-9])$/.exec(dateStr);
      day = res[1]; month = res[2]; year = '20'+res[3];
    }
    return year+'-'+month+'-'+day;
  }

  checkDate(dateStr: string) {
    if (/^[0-3][0-9]\/[0-1][0-9]\/[0-2][0-9][0-9][0-9]$/.test(dateStr) || // FORMAT dd/MM/yyyy
        /^[0-2][0-9][0-9][0-9]\-[0-1][0-9]\-[0-3][0-9]$/.test(dateStr) || // FORMAT yyyy-MM-dd
        /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9]$/.test(dateStr)) { // FORMAT dd/MM/yy
      return true;
    }
    return false;
  }

  async parseFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      this.parseFile(files[i]);
    }
  }

  parseFile(file: File) {
    parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result,) => {
        let dataList = result.data;
        // Filter data to keep only elements with valid date
        dataList = dataList.filter(
          (elem) => {
            if (elem.date != undefined) {
              return this.checkDate(elem.date);
            }
            return false;
          }
        ).map(
          (elem) => {
            elem.date = this.formatDate(elem.date);
            return elem;
          }
        )
        for (let key in dataList[0]) { // Iteration over column names
          if(key != 'date' && key != '') {
            let fund = new Product(key);
            fund.is_private = true;
            const frequency_alias = fund.initFromParsedCSVData(dataList);
            this.uploadedProducts.push(fund);
          }
        }
      }
    });
  }

  async onUpdate() {
    this.notificationsService.runSpinner();

    // Upload portfolio in the cloud, computes analytics and returns the new cluster ID
    this.clustersService.updateCluster({
      original: this.managedOriginal,
      benchmark: this.managedBenchmark,
      components: this.managedComponents
    }).then(
      () => {this.notificationsService.notify('cluster/update-success');},
      () => {this.notificationsService.notify('cluster/update-failed');},
    ).then(() => {
      this.notificationsService.stopSpinner();
    });
  }

  onClusterChange(currentCluster: Cluster) {
    // No update if cluster has not been to investable solution step
    this.hasInvestable = currentCluster.has_investable;

    // Restructurate data for update
    this.managedOriginal = {
      original_data: currentCluster.original_description,
      updated_data: undefined
    } as ManagedFund;
    this.managedBenchmark = {
      original_data: currentCluster.benchmark_description,
      updated_data: undefined
    } as ManagedFund;
    this.managedComponents = currentCluster.components_description.map((description) => {
      return {
        original_data: description,
        updated_data: undefined
      } as ManagedFund;
    });

    // Check if data needed to be uploaded
    let fileUploadNeeded: boolean = false;
    if (!this.currentCluster.is_standalone) {
      fileUploadNeeded = fileUploadNeeded || (!this.currentCluster.is_backtest && this.managedOriginal.original_data.is_private);
      this.managedComponents.forEach((managed) => {
        fileUploadNeeded = fileUploadNeeded || managed.original_data.is_private;
      });
    }
    fileUploadNeeded = fileUploadNeeded || this.managedBenchmark.original_data.is_private;
    this.fileUploadNeeded = fileUploadNeeded;
  }

  onUpdateSelectionChange() {
    let fileUploadLacking: boolean = false;
    if (!this.currentCluster.is_standalone) {
      fileUploadLacking = fileUploadLacking || (!this.currentCluster.is_backtest && this.managedOriginal.original_data.is_private && this.managedOriginal.updated_data==undefined);
      this.managedComponents.forEach((managed) => {
        fileUploadLacking = fileUploadLacking || managed.original_data.is_private && managed.updated_data==undefined;
      });
    }
    fileUploadLacking = fileUploadLacking || (this.managedBenchmark.original_data.is_private && this.managedBenchmark.updated_data==undefined);
    this.fileUploadLacking = fileUploadLacking;

    this.disableUpdate = !this.hasInvestable || fileUploadLacking;
  }

}
